import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box, Alert } from '@mui/material';
import { Warning, CheckCircle, Close } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { useQuery, useQueryClient } from 'react-query';

const SkillLevelNotification = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [notification, setNotification] = useState(null);

  // Fetch unread skill level notifications (team dashboard only; players use player notifications)
  useQuery(
    ['skillLevelNotifications', user?.id],
    async () => {
      if (!user?.id) return null;
      const response = await api.get('/dashboard/notifications?type=skill_level_update_required&limit=1');
      return response.data;
    },
    {
      enabled: !!user?.id && user?.type !== 'player',
      refetchInterval: 30000, // Check every 30 seconds
      onSuccess: (data) => {
        if (data?.data && data.data.length > 0) {
          const unreadNotif = data.data.find(n => !n.is_read && n.type === 'skill_level_update_required');
          if (unreadNotif) {
            setNotification(unreadNotif);
            setOpen(true);
          } else {
            setNotification(null);
            setOpen(false);
          }
        } else {
          setNotification(null);
          setOpen(false);
        }
      }
    }
  );

  // Check if team has skill level (notification should disappear)
  useEffect(() => {
    if (user?.team_skill_level && user.team_skill_level > 0 && notification) {
      // Team now has skill level, mark notification as read and close
      markAsRead();
    }
  }, [user?.team_skill_level, notification]);

  const markAsRead = async () => {
    if (!notification?.id) return;
    
    try {
      await api.put(`/dashboard/notifications/${notification.id}/read`);
      setOpen(false);
      setNotification(null);
      queryClient.invalidateQueries(['skillLevelNotifications', user?.id]);
      queryClient.invalidateQueries(['dashboard', 'overview']); // Refresh team data
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleClose = () => {
    markAsRead();
  };

  const handleGoToMembers = () => {
    markAsRead();
    // Navigate to members section (you may need to adjust this based on your routing)
    window.location.hash = '#members';
    // Or use your router if you have one
    // navigate('/dashboard#members');
  };

  if (!notification || !open) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1,
        pb: 1,
        borderBottom: '1px solid #e0e0e0'
      }}>
        <Warning sx={{ color: '#f59e0b', fontSize: 28 }} />
        <Typography variant="h6" component="div" sx={{ flex: 1, fontWeight: 600 }}>
          Action Required
        </Typography>
        <Button
          onClick={handleClose}
          sx={{ minWidth: 'auto', padding: '4px' }}
          size="small"
        >
          <Close />
        </Button>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 3 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
            {notification.title}
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
            {notification.message}
          </Typography>
        </Alert>
        
        <Box sx={{ mt: 2, p: 2, bgcolor: '#f9fafb', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>Why is this important?</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Without skill levels for both team members, we cannot assign your team to a division, 
            and you may not be able to participate in matches for the upcoming season.
          </Typography>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
        <Button onClick={handleClose} variant="outlined">
          Remind Me Later
        </Button>
        <Button 
          onClick={handleGoToMembers} 
          variant="contained"
          startIcon={<CheckCircle />}
          sx={{
            bgcolor: '#f59e0b',
            '&:hover': {
              bgcolor: '#d97706'
            }
          }}
        >
          Update Skill Levels
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SkillLevelNotification;

